import React from 'react';
import Box from '@nubank/nuds-web/components/Box/Box';
import styled from 'styled-components';
import Image from '@nubank/nuds-web/components/Image/Image';
import Typography from '@nubank/nuds-web/components/Typography/Typography';

import { FROZEN_TIME, useDynamicYieldValues } from '../../../../utils/dynamicYield';

const Container = styled.div`
  display: grid;
  grid-template-columns: 420px 230px repeat(2, minmax(200px, 320px));
  grid-row-gap: 18px;
  grid-template-rows: repeat(5, 1fr);
  justify-content: center;
`;

const StyledRow = styled(Box)`
  min-height: 112px;
  background-color: #EFEFEF;
  display: flex;
  align-items: center;
  justify-content: center;

  &.opacity-80 {
    background-color: rgba(239 239 239 / 80%);
  }

  &.opacity-60 {
    background-color: rgba(239 239 239 / 60%);
  }

  &.opacity-40 {
    background-color: rgba(239 239 239 / 40%);
  }

  &.opacity-30 {
    background-color: rgba(239 239 239 / 30%);
  }

  &.opacity-full-purple {
    background-color: #7B4DD6;
  }

  &.title {
    background-color: #FFF;
    flex-direction: column;
  }

  &.border-left {
    border-top-left-radius: 15px;
    border-bottom-left-radius: 15px;
    justify-content: left;
  }

  &.border-right {
    border-top-right-radius: 15px;
    border-bottom-right-radius: 15px;
    justify-content: left;
    padding-left: 40px;
  }
`;

const StyledImage = styled(Image)`
  margin-left: -25px;
  margin-right: 20px;
`;

const StyledRowTitle = styled(Typography)`
  font-size: 24px;
  font-weight: 500;
  line-height: 28.8px;
  letter-spacing: -0.02em;

  span {
    color: #7B4DD6;
  }
`;

const StyledRowSubtitle = styled(Typography)`
  font-size: 16px;
  font-style: italic;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: -0.01em;
  margin-top: 8px;
`;

const StyledRowFirstColumnText = styled(Typography)`
  font-size: 24px;
  font-weight: 500;
  line-height: 28.8px;
  letter-spacing: -0.02em;
`;

const StyledRowPercentage = styled(Typography)`
  padding: 4px 8px;
  padding-bottom: 4px;
  border-radius: 5px;
  background-color: #7B4DD6;
  color: #fff;
  font-size: 24px;
  font-weight: 500;
  line-height: 28.8px;
  letter-spacing: -0.02em;
`;

const StyledRowPercentageInverted = styled(Typography)`
  padding: 4px 8px;
  padding-bottom: 4px;
  border-radius: 5px;
  background-color: white;
  color: #7B4DD6;
  font-size: 24px;
  font-weight: 500;
  line-height: 28.8px;
  letter-spacing: -0.02em;
`;

const StyledGatPercentage = styled(Typography)`
font-size: 24px;
font-weight: 500;
line-height: 28.8px;
letter-spacing: -0.02em;

 span {
    font-size: 18px;
    line-height: 23.4px;
    letter-spacing: -0.01em;
  }
`;

function DesktopYieldTable() {
  const { values: dynamicYieldValues } = useDynamicYieldValues();

  return (
    <Box
      display={{
        xs: 'none',
        lg: 'block',
      }}
      marginTop="120px"
      // marginHorizontal="100px"
    >
      <Container>
        {/* ROW 1 */}
        <StyledRow className="title" />
        <StyledRow className="title">
          <StyledRowTitle
            strong
            whiteSpace="pre-line"
            textAlign="center"
          >
            {'Tasa de \n '}
            <span>Rendimiento</span>
            {'\n Anual Fija'}
          </StyledRowTitle>
        </StyledRow>

        <StyledRow className="title">
          <StyledRowTitle strong>
            GAT Nominal
          </StyledRowTitle>
          <StyledRowSubtitle whiteSpace="pre-line">
            {'Es el porcentaje de \n rendimiento antes \n de impuestos.'}
          </StyledRowSubtitle>
        </StyledRow>

        <StyledRow className="title">
          <StyledRowTitle strong>
            GAT Real
            <StyledRowSubtitle whiteSpace="pre-line">
              {'Es el rendimiento que \n obtendría después de descontar\n la inflación estimada.'}
            </StyledRowSubtitle>
          </StyledRowTitle>

        </StyledRow>

        {/* ROW 2 */}

        <StyledRow className="border-left opacity-full-purple">
          <StyledImage
            alt="icono de ahorro congelado"
            src="cuenta/transparency/cajita.svg"
            width="50px"
            webp={false}
          />
          <StyledRowFirstColumnText strong width="100%" color="white">
            Cajita Turbo
            <sup>¹</sup>
          </StyledRowFirstColumnText>
        </StyledRow>

        <StyledRow className="opacity-full-purple">
          <StyledRowPercentageInverted strong>
            {`${dynamicYieldValues.dynamicYieldTurbo}%`}
          </StyledRowPercentageInverted>
        </StyledRow>

        <StyledRow className="opacity-full-purple">
          <StyledGatPercentage strong color="white">
            {`${dynamicYieldValues.dynamicGATNominalTurbo}% `}
          </StyledGatPercentage>

        </StyledRow>
        <StyledRow className="border-right opacity-full-purple">
          <StyledGatPercentage strong color="white">
            {`${dynamicYieldValues.dynamicGATRealTurbo}% `}
          </StyledGatPercentage>
        </StyledRow>

        {/* ROW 3 */}

        <StyledRow className="border-left opacity-80">
          <StyledImage
            alt="icono de ahorro congelado"
            src="cuenta/transparency/cajita.svg"
            width="50px"
            webp={false}
          />
          <StyledRowFirstColumnText strong width="100%">
            Cajitas Nu
            <sup>²</sup>
          </StyledRowFirstColumnText>
        </StyledRow>

        <StyledRow className="opacity-80">
          <StyledRowPercentage strong>
            {`${dynamicYieldValues.dynamicYield}%`}
          </StyledRowPercentage>
        </StyledRow>

        <StyledRow className="opacity-80">
          <StyledGatPercentage strong>
            {`${dynamicYieldValues.dynamicGATNominal}% `}
          </StyledGatPercentage>

        </StyledRow>
        <StyledRow className="border-right opacity-80">
          <StyledGatPercentage strong>
            {`${dynamicYieldValues.dynamicGATReal}% `}
          </StyledGatPercentage>
        </StyledRow>

        {/* ROW 4 */}
        <StyledRow className="border-left opacity-60">
          <StyledImage
            alt="icono de ahorro congelado"
            src="cuenta/transparency/frozen.svg"
            width="50px"
            webp={false}
          />
          <StyledRowFirstColumnText strong>
            Ahorro Congelado:
            <StyledRowFirstColumnText tag="span" strong color="#7B4DD6">
              {` ${FROZEN_TIME.A_7_DAYS} días`}
              <sup>³</sup>
            </StyledRowFirstColumnText>
          </StyledRowFirstColumnText>
        </StyledRow>

        <StyledRow className="opacity-60">
          <StyledRowPercentage strong>
            {`${dynamicYieldValues.dynamicYield7Days}%`}
          </StyledRowPercentage>
        </StyledRow>

        <StyledRow className="opacity-60">
          <StyledGatPercentage strong>
            {`${dynamicYieldValues.dynamicGATNominal7Days}% `}
          </StyledGatPercentage>

        </StyledRow>
        <StyledRow className="border-right opacity-60">
          <StyledGatPercentage strong>
            {`${dynamicYieldValues.dynamicGATReal7Days}% `}
          </StyledGatPercentage>
        </StyledRow>

        {/* ROW 5 */}

        <StyledRow className="border-left opacity-40">
          <StyledImage
            alt="icono de ahorro congelado"
            src="cuenta/transparency/frozen.svg"
            width="50px"
            webp={false}
          />
          <StyledRowFirstColumnText strong>
            Ahorro Congelado:
            <StyledRowFirstColumnText tag="span" strong color="#7B4DD6">
              {` ${FROZEN_TIME.A_28_DAYS} días`}
              <sup>³</sup>
            </StyledRowFirstColumnText>
          </StyledRowFirstColumnText>
        </StyledRow>

        <StyledRow className="opacity-40">
          <StyledRowPercentage strong>
            {`${dynamicYieldValues.dynamicYield28Days}%`}
          </StyledRowPercentage>
        </StyledRow>

        <StyledRow className="opacity-40">
          <StyledGatPercentage strong>
            {`${dynamicYieldValues.dynamicGATNominal28Days}% `}
          </StyledGatPercentage>

        </StyledRow>
        <StyledRow className="border-right opacity-40">
          <StyledGatPercentage strong>
            {`${dynamicYieldValues.dynamicGATReal28Days}% `}
          </StyledGatPercentage>
        </StyledRow>

        {/* ROW 6 */}

        <StyledRow className="border-left opacity-40">
          <StyledImage
            alt="icono de ahorro congelado"
            src="cuenta/transparency/frozen.svg"
            width="50px"
            webp={false}
          />
          <StyledRowFirstColumnText strong>
            Ahorro Congelado:
            <StyledRowFirstColumnText tag="span" strong color="#7B4DD6">
              {` ${FROZEN_TIME.A_90_DAYS} días`}
              <sup>³</sup>
            </StyledRowFirstColumnText>
          </StyledRowFirstColumnText>
        </StyledRow>

        <StyledRow className="opacity-40">
          <StyledRowPercentage strong>
            {`${dynamicYieldValues.dynamicYield90Days}%`}
          </StyledRowPercentage>

        </StyledRow>

        <StyledRow className="opacity-40">
          <StyledGatPercentage strong>
            {`${dynamicYieldValues.dynamicGATNominal90Days}% `}
          </StyledGatPercentage>

        </StyledRow>
        <StyledRow className="border-right opacity-40" justifyContent="left">
          <StyledGatPercentage strong>
            {`${dynamicYieldValues.dynamicGATReal90Days}% `}
          </StyledGatPercentage>
        </StyledRow>

        {/* ROW 7 */}

        <StyledRow className="border-left opacity-40">
          <StyledImage
            alt="icono de ahorro congelado"
            src="cuenta/transparency/frozen.svg"
            width="50px"
            webp={false}
          />
          <StyledRowFirstColumnText strong>
            Ahorro Congelado:
            <StyledRowFirstColumnText tag="span" strong color="#7B4DD6">
              {` ${FROZEN_TIME.A_180_DAYS} días`}
              <sup>³</sup>
            </StyledRowFirstColumnText>
          </StyledRowFirstColumnText>
        </StyledRow>

        <StyledRow className="opacity-30">
          <StyledRowPercentage strong>
            {`${dynamicYieldValues.dynamicYield180Days}%`}
          </StyledRowPercentage>
        </StyledRow>

        <StyledRow className="opacity-30">
          <StyledGatPercentage strong>
            {`${dynamicYieldValues.dynamicGATNominal180Days}% `}
          </StyledGatPercentage>

        </StyledRow>
        <StyledRow className="border-right opacity-30">
          <StyledGatPercentage strong>
            {`${dynamicYieldValues.dynamicGATReal180Days}% `}
          </StyledGatPercentage>
        </StyledRow>

      </Container>
    </Box>
  );
}

export default DesktopYieldTable;
